import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Container,
  Alert,
  Divider,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import logoPainel from '../imagens/logopainelftm.png.ico';

const LoginContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: theme.palette.background.default,
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  background: 'rgba(17, 24, 39, 0.7)',
  backdropFilter: 'blur(10px)',
  padding: theme.spacing(4),
  borderRadius: '16px',
  border: '1px solid rgba(124, 58, 237, 0.2)',
  maxWidth: '400px',
  width: '100%',
  margin: 'auto',
  marginTop: theme.spacing(8)
}));

export default function Login({ onLogin }) {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const googleBtnRef = useRef(null);

  const handleGoogleResponse = useCallback(async (response) => {
    setError('');
    setGoogleLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/google`,
        { credential: response.credential },
        { timeout: 15000 }
      );
      if (res.data.success && res.data.token) {
        localStorage.setItem('user', JSON.stringify(res.data.user));
        localStorage.setItem('token', res.data.token);
        onLogin(res.data.user);
        navigate('/dashboard');
      } else if (res.data.needsCpf) {
        navigate('/register', { state: { googleCredential: response.credential, googleData: res.data.googleData } });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao autenticar com Google.');
    } finally {
      setGoogleLoading(false);
    }
  }, [navigate, onLogin]);

  useEffect(() => {
    const initGoogle = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
        });
        if (googleBtnRef.current) {
          window.google.accounts.id.renderButton(googleBtnRef.current, {
            type: 'standard',
            theme: 'filled_black',
            size: 'large',
            text: 'continue_with',
            width: '100%',
            locale: 'pt-BR',
          });
        }
      }
    };
    if (window.google?.accounts?.id) {
      initGoogle();
    } else {
      const interval = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(interval);
          initGoogle();
        }
      }, 200);
      return () => clearInterval(interval);
    }
  }, [handleGoogleResponse]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/login`,
        { email: formData.email, senha: formData.password },
        { timeout: 15000 }
      );
      if (response.data.user && response.data.token) {
        const userData = { ...response.data.user };
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('token', response.data.token);
        onLogin(userData);
        navigate('/dashboard');
      }
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        setError('Servidor demorou para responder. Tente novamente.');
      } else {
        setError(error.response?.data?.message || 'Erro ao fazer login. Verifique sua conexão.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <StyledPaper elevation={3}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <img src={logoPainel} alt="Painel FTM" style={{ width: 64, height: 64, borderRadius: 12 }} />
        </Box>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#fff', textAlign: 'center', mb: 4 }}>
          Login
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Google Sign-In */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          {googleLoading ? (
            <CircularProgress size={32} sx={{ color: '#7C3AED', my: 1 }} />
          ) : (
            <Box ref={googleBtnRef} sx={{ width: '100%', display: 'flex', justifyContent: 'center' }} />
          )}
        </Box>

        <Divider sx={{ my: 2, '&::before, &::after': { borderColor: 'rgba(124, 58, 237, 0.2)' } }}>
          <Typography sx={{ color: '#6B7280', fontSize: '0.85rem', px: 1 }}>ou</Typography>
        </Divider>

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth label="Email" name="email" type="email"
            value={formData.email} onChange={handleChange} margin="normal" required
          />
          <TextField
            fullWidth label="Senha" name="password" type="password"
            value={formData.password} onChange={handleChange} margin="normal" required
          />
          <Button
            type="submit" fullWidth variant="contained" disabled={loading}
            sx={{
              mt: 3, mb: 1, background: 'linear-gradient(135deg, #7C3AED 0%, #4B1FA6 100%)',
              color: '#fff', padding: '12px', borderRadius: '12px', textTransform: 'none',
              fontSize: '1rem', fontWeight: 600,
              '&:hover': { background: 'linear-gradient(135deg, #8B5CF6 0%, #5B21B6 100%)' }
            }}
          >
            {loading ? 'Entrando...' : 'Entrar'}
          </Button>
        </form>

        <Box sx={{ textAlign: 'center', mt: 1 }}>
          <Button
            variant="text" size="small"
            onClick={() => navigate('/forgot-password')}
            sx={{ color: '#9CA3AF', textTransform: 'none', fontSize: '0.85rem', '&:hover': { color: '#A78BFA' } }}
          >
            Esqueceu a senha?
          </Button>
        </Box>

        <Box sx={{ mt: 1, textAlign: 'center' }}>
          <Typography variant="body2">
            Ainda não tem uma conta?{' '}
            <Button variant="text" sx={{ color: 'primary.main', textTransform: 'none', fontWeight: 600 }} onClick={() => navigate('/register')}>
              Cadastre-se
            </Button>
          </Typography>
        </Box>
      </StyledPaper>
    </LoginContainer>
  );
}
